import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { AllocatableLease, DistributionResult } from '@modules/private/payment/components/lease-distribution-selector/lease-distribution-selector.component'
import { APPLICATION_WARNING_LABELS, CONCEPT_LABELS, PERIOD_RESULT_LABELS, explainApplicationError } from '@modules/private/application-messages'
import { PaymentRow } from '../../../data/interfaces/lease-ledger.interface'

export type PreviewMode = 'APPLY' | 'RESIDUAL' | 'CANCEL'

@Component({
	selector: 'app-apply-preview-dialog',
	templateUrl: './apply-preview-dialog.component.html',
	styleUrls: ['./apply-preview-dialog.component.scss'],
})
export class ApplyPreviewDialogComponent implements OnChanges {
	@Input() visible = false
	@Input() mode: PreviewMode = 'APPLY'
	/** Candidatos: uno (cajón, letra con un solo pago) o varios (franja). Elegir pago sí; elegir letra no. */
	@Input() payments: PaymentRow[] = []
	//* B-039 · `letters`: todas las letras a las que se aplicó; `n` es la primera y queda de respaldo.
	@Input() application: { applicationId: string, n: number | null, letters?: number[], amount: number } | null = null
	@Input() leaseId = ''
	/**
	 * Los arrendamientos ACTIVOS del cliente, para repartir el pago entre varios (D3/D7).
	 *
	 * Llegan desde el padre porque él es quien sabe de qué cliente se trata y quien puede pedirlos. Con uno
	 * solo el selector no aparece y el diálogo se comporta como siempre: ahí no hay reparto que decidir.
	 */
	@Input() clientLeases: AllocatableLease[] = []
	/** Primera letra por cobrar y su fecha (`ledger.nextToCollect`, PRD §7.1): el aviso FIFO del diseño. */
	@Input() nextToCollect: number | null = null
	@Input() nextToCollectDate: string | null = null
	@Input() previewing = false
	@Input() saving = false

	//* Dos vías (ver edit-amounts-modal.component.ts:46-48).
	/** Lo último que emitió el selector. Null mientras no haya varios arrendamientos que repartir. */
	distribution: DistributionResult | null = null

	@Output() visibleChange = new EventEmitter<boolean>()
	@Output() onHide = new EventEmitter<void>()
	@Output() onPreview = new EventEmitter<{ mode: PreviewMode, paymentId: string, applicationId?: string, payload: any }>()
	@Output() onApply = new EventEmitter<{ mode: PreviewMode, paymentId: string, applicationId?: string, payload: any }>()

	paymentId = ''
	/**
	 * «Monto a aplicar» (diseño aprobado, primer campo): por omisión todo lo disponible del pago. Un monto menor
	 * viaja como `specificAmount` (`LeaseApplicationDto`, `application.dto.ts:15-23`, `@Min(0.01)`) y deja el resto
	 * disponible para otro arrendamiento del cliente. Elegir monto sí; elegir letra no (D6): la prelación y el
	 * orden FIFO los pone el backend.
	 */
	amount: number | null = null
	reason = ''
	preview: any = null
	previewError = ''
	confirmations: Record<string, boolean> = {}
	confirmingClose = false

	readonly titles: Record<PreviewMode, string> = { APPLY: 'Aplicar pago', RESIDUAL: 'Aplicar al residual', CANCEL: 'Cancelar aplicación' }
	/**
	 * Traducción de los errores del motor a algo que se pueda actuar.
	 *
	 * El backend lanza `BadRequestException` con un texto pensado para quien lee el código
	 * («Inconsistencia en montos a revertir»), y la pantalla lo enseñaba tal cual: el operador no
	 * sabía si se había equivocado, si faltaba un dato o si tenía que llamar a alguien. Cada entrada
	 * dice QUÉ pasó y CÓMO sigue; el texto original se conserva siempre como detalle técnico, así que
	 * si el backend cambia una frase lo peor que ocurre es que se vea lo mismo que se veía antes.
	 */

	/** Cómo queda la letra tras aplicar, en español. El motor devuelve el estado del periodo en crudo. */
	readonly resultingLabels = PERIOD_RESULT_LABELS

	//* Los tres diccionarios del motor viven en UNO solo (`application-messages`): estaban copiados
	//* aquí y en el asistente, y ya habían divergido — a esta copia le faltaba `LEASE_WILL_SETTLE`.
	readonly warningLabels = APPLICATION_WARNING_LABELS
	readonly conceptLabels = CONCEPT_LABELS

	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (this.visible) this.requestHide()
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['visible'] && this.visible) this.reset()
	}

	private reset(): void {
		this.paymentId = this.payments[0]?._id ?? ''
		this.amount = this.payment?.pendingToApply ?? null
		this.reason = ''
		this.confirmingClose = false
		this.invalidatePreview()
	}

	get payment(): PaymentRow | null {
		return this.payments.find((p) => p._id === this.paymentId) ?? null
	}

	/** Al cambiar de pago el monto vuelve al disponible de ese pago. */
	onPaymentChange(): void {
		this.amount = this.payment?.pendingToApply ?? null
		this.invalidatePreview()
	}

	/** Cualquier cambio en la captura tira la previsualización: el usuario debe recalcular. */
	invalidatePreview(): void {
		this.preview = null
		this.previewError = ''
		this.confirmations = {}
	}

	get canPreview(): boolean {
		if (this.mode === 'CANCEL') return !!this.application?.applicationId
		const available = this.payment?.pendingToApply ?? 0
		const base = !!this.paymentId && !!this.leaseId && Number(this.amount) > 0 && Number(this.amount) <= available + 0.005
		//* Con varios destinos no se previsualiza hasta que el operador confirme el reparto: el impacto que se
		//* calcularía sería el de un reparto que todavía puede cambiar.
		if (!this.showsSelector) return base
		return base && !!this.distribution && this.distribution.allocations.length > 0 && !this.distribution.needsConfirmation
	}

	/** Suma por letra del plan (columna «Aplicado» del diseño). */
	applied(row: any): number {
		return Object.values((row?.byConcept ?? {}) as Record<string, number>).reduce((s, v) => s + (Number(v) || 0), 0)
	}

	/** Total que se va a aplicar según el plan: va en el botón «Aplicar $X». */
	get totalPlan(): number {
		return ((this.preview?.plan ?? []) as any[]).reduce((s, r) => s + this.applied(r), 0)
	}

	requestPreview(): void {
		this.previewError = ''
		this.onPreview.emit(this.event())
	}

	/** El padre entrega el resultado del dryRun. */
	setPreview(result: any, error?: string): void {
		if (error) {
			this.preview = null
			this.previewError = error
			return
		}
		this.preview = result
		this.confirmations = {}
	}

	/**
	 * El error de la previsualización, en palabras. Sin entrada en el diccionario se conserva el
	 * comportamiento de antes —el mensaje del backend tal cual— más qué hacer con él.
	 */
	get previewErrorExplained(): { titulo: string, que: string, comoSigo: string, detalle: string } | null {
		return explainApplicationError(this.previewError)
	}

	get blockingWarnings(): any[] {
		return (this.preview?.warnings || []).filter((w: any) => w.blocking)
	}

	get allWarningsConfirmed(): boolean {
		return this.blockingWarnings.every((w: any) => this.confirmations[w.code])
	}

	/** Aplicar no exige motivo (PRD E2); cancelar sí. */
	get canApply(): boolean {
		return !!this.preview && (this.mode !== 'CANCEL' || !!this.reason.trim()) && this.allWarningsConfirmed && !this.saving
	}

	buildPayload(): any {
		const base: any = {}
		if (this.preview) {
			base.stateHash = this.preview.stateHash
			base.confirmations = Object.keys(this.confirmations).filter((k) => this.confirmations[k])
		}
		if (this.mode === 'CANCEL') {
			//* Sin motivo no viaja `reason`: el dryRun funciona sin él (E4) y el cuerpo queda vacío hasta previsualizar.
			const reason = this.reason.trim()
			if (reason) base.reason = reason
			return base
		}
		//* CreateApplicationDto (application.dto.ts:36-77): `user` ya no viaja, el actor sale del JWT (F22).
		base.payment = this.paymentId
		//* `specificAmount` solo cuando el monto capturado es menor que el disponible: con todo el disponible el
		//* backend aplica automáticamente (`LeaseApplicationDto.specificAmount?`, «si no se especifica se aplica automáticamente»).
		const specific = Number(this.amount)
		const available = this.payment?.pendingToApply ?? 0
		if (this.showsSelector && this.distribution) {
			//* Con reparto, el ORDEN del arreglo es el que el operador puso arrastrando: en cascada decide a
			//* quién le llega el dinero primero. Sin tope, no se manda `specificAmount` por arrendamiento: el
			//* motor reparte con `distribution` y puede cascadear más allá de lo que el panel estimó.
			base.distribution = this.distribution.distribution

			//! Cuando el operador aplica MENOS de lo disponible, el tope tiene que ir arrendamiento por
			//! arrendamiento. Antes se ponía solo en `leases[0]` —«el tope va en el conjunto»— y eso no
			//! existe en el motor: un arrendamiento sin `specificAmount` recibe todo lo que queda del pago
			//! (`applications.service.ts`, rama de cascada) o su parte de lo que sobra tras los topes (rama
			//! de partes iguales). Con un pago de $50,000 disponible, escribir $20,000 y repartir entre dos
			//! aplicaba los $50,000 ENTEROS: $20,000 al primero y $30,000 al segundo.
			//!
			//! Se mandan los importes que el operador acaba de ver y confirmar, y se omiten los que reciben
			//! cero: un `specificAmount` de 0 es falsy y el motor lo leería como «sin tope», que es justo el
			//! agujero que esto cierra.
			const conTope = specific > 0 && Math.abs(specific - available) > 0.005
			base.leases = this.distribution.allocations
				.filter((a) => !conTope || a.amount > 0)
				.map((a) => (conTope ? { lease: a.leaseId, specificAmount: a.amount } : { lease: a.leaseId }))
		} else {
			base.leases = [specific > 0 && Math.abs(specific - available) > 0.005 ? { lease: this.leaseId, specificAmount: specific } : { lease: this.leaseId }]
		}
		if (this.mode === 'RESIDUAL') base.applicationType = 'RESIDUAL_PAYMENT'
		return base
	}

	private event() {
		return { mode: this.mode, paymentId: this.paymentId, applicationId: this.application?.applicationId, payload: this.buildPayload() }
	}

	/** El selector solo tiene sentido cuando hay más de un destino posible y se está aplicando. */
	/**
	 * Si la tabla de impacto tiene que decir a qué arrendamiento pertenece cada letra.
	 *
	 * Solo cuando el plan toca más de uno. Repartiendo un pago entre dos contratos, la tabla mostraba
	 * dos filas «letra 1» idénticas salvo por el «queda», y el operador aprobaba un impacto que no
	 * podía atribuir. Con un solo arrendamiento la columna sobra y solo estorba, que es el caso normal.
	 */
	get showLeaseColumn(): boolean {
		const ids = new Set(((this.preview?.plan ?? []) as any[]).map((r) => String(r.leaseId ?? '')))
		ids.delete('')
		return ids.size > 1
	}

	/** El folio del arrendamiento de una letra del plan. Cae al id corto si no lo tenemos a mano. */
	folioOf(leaseId: string): string {
		const id = String(leaseId ?? '')
		return this.clientLeases.find((l) => l.leaseId === id)?.folio || (id ? '…' + id.slice(-6) : '—')
	}

	get showsSelector(): boolean {
		return this.mode === 'APPLY' && this.clientLeases.length > 1
	}

	/**
	 * Cambiar el reparto tira la previsualización.
	 *
	 * La huella de estado se calculó sobre unos arrendamientos concretos: si cambian el orden, las casillas
	 * o el modo, el plan que está en pantalla ya no es el que se guardaría.
	 */
	onDistributionChange(result: DistributionResult): void {
		const cambio = !this.distribution || JSON.stringify(this.distribution.allocations) !== JSON.stringify(result.allocations) || this.distribution.distribution !== result.distribution
		this.distribution = result
		if (cambio && this.preview) this.invalidatePreview()
	}

	apply(): void {
		if (!this.canApply) return
		this.onApply.emit(this.event())
	}

	hide(): void {
		this.confirmingClose = false
		this.visible = false
		this.visibleChange.emit(false)
		this.onHide.emit()
	}

	get isDirty(): boolean {
		const available = this.payment?.pendingToApply ?? null
		return !!this.preview || !!this.reason.trim() || (this.amount !== null && available !== null && Math.abs(Number(this.amount) - available) > 0.005)
	}

	requestHide(): void {
		if (this.isDirty && !this.confirmingClose) {
			this.confirmingClose = true
			return
		}
		this.hide()
	}

	cancelClose(): void {
		this.confirmingClose = false
	}
}
